import React, {FC, PropsWithChildren, useEffect} from 'react';
import {useLocalObservable} from 'mobx-react-lite';
import {getLocalId} from 'utils/localId';

import {Holder, Status} from './holder';
import {useHold} from './query.hold';
import {useRelease} from './query.release';

const reholdInterval = 5 * 1000;

interface ProviderProps {
	holder: Holder
}

const HoldContext = React.createContext<Holder>(new Holder(''));

export const HolderProvider: FC<PropsWithChildren<ProviderProps>> = ({children, holder}) => {
	return (
		<HoldContext.Provider value={holder}>
			{children}
		</HoldContext.Provider>
	);
};

export const useContextHolder = () => React.useContext(HoldContext);

export const useCreateHolder = (appt: string, holdId?: string) => {
	const holder = useLocalObservable(() => new Holder(holdId || getLocalId()));
	const [release] = useRelease();

	// Hold results should update our expire reference
	const [refresh] = useHold({
		onCompleted: (data) => {
			const {hold} = data.payment;
			if (hold.error) {
				holder.setError(hold.error);
			} else if (!hold.remaining || hold.remaining <= 0) {
				holder.setRemaining(0);
			} else {
				holder.setRemaining(hold.remaining);
			}

			if (hold.created) {
				holder.setCreated(hold.created);
			}
		},
		onError: () => {
			holder.setError('Could not contact server.');
		},
	});

	// Manage API calls to hold reservation
	useEffect(() => {
		const runnable = Boolean(appt && holder.id);
		holder.reset(runnable);

		// Early exit if there's nothing to do
		if (!runnable) {
			return;
		}

		refresh({variables: {
			appt,
			hold: holder.id,
			init: true,
		}});

		const interval = setInterval(
			() => {
				if (holder.status === Status.pending) {
					return;
				} else if (holder.status !== Status.running) {
					clearInterval(interval);
				} else {
					refresh({variables: {
						appt,
						hold: holder.id,
						init: false,
					}});
				}
			},
			reholdInterval,
		);

		return () => clearInterval(interval);
	}, [holder, appt, holder.id]);

	// Manage API calls to release reservation
	useEffect(() => {
		if (holder.created) {
			return () => {
				release({variables: {
					hold: holder.id,
					created: holder.created,
				}});
			};
		}
	}, [holder.id, holder.created]);

	return holder;
};
